/**
 * M6 — Reports View
 *
 * Full reporting UI: standard reports grid, saved reports list,
 * custom report builder with filters, and multi-format export.
 */
import { type ReactNode, useEffect, useRef, useState } from 'react'

import { Q12ReportBody } from '../../../../../common/frontend/components/q12-results/q12-report-body.js'
import { t } from '../../../../../common/i18n.js'
import { Language } from '../../../../../common/types/i18n.js'
import type { Q12ReportData } from '../../../../../common/types/q12-report.js'
import { Button } from '../../../../components/button/button.js'
import { MultiSelect, Select } from '../../../../components/forms/select/select.js'
import { TextField } from '../../../../components/forms/text-field/text-field.js'
import {
    ArrowLeft,
    ArrowRight,
    Cake,
    Calendar,
    ClipboardList,
    Clock,
    Coins,
    Receipt,
    Smile,
    Trophy,
    Users,
    X,
} from '../../../../components/icon/solar-icons.js'
import { PageHeader } from '../../../../components/page-header/page-header.js'
import { PageShell } from '../../../../components/page-shell/page-shell.js'
import { printPage } from '../../../../utils/print-page.js'
import { portalDelete, portalDownload, portalGet, portalPost } from '../../utils/api.js'
import './reports-view.css'

// ── Types ──

interface ReportColumn {
    key: string
    label: string
    category: string
}

interface ReportRow {
    [key: string]: unknown
}

interface SavedReport {
    id: number
    name: string
    config: string
    created_at_utc: string
    updated_at_utc: string
}

interface StandardReportDef {
    slug: string
    icon: ReactNode
    nameKey: keyof typeof t.portal.reports
    descKey: keyof typeof t.portal.reports
    /** Private-tier salary data — only shown to payroll-capable users. */
    requiresPayroll?: boolean
}

interface RefOption {
    id: number
    name: string
}

interface BuilderFilters {
    teamIds: number[]
    companyIds: number[]
    status: string | null
    startDate: string
    endDate: string
}

type ViewMode = 'index' | 'detail' | 'builder' | 'payroll' | 'q12'

// ── Standard report definitions ──// ── Standard report definitions ──

const STANDARD_REPORTS: StandardReportDef[] = [
    {
        slug: 'absences-summary',
        icon: <ClipboardList size={20} />,
        nameKey: 'absencesSummary',
        descKey: 'absencesSummaryDesc',
    },
    {
        slug: 'headcount-snapshot',
        icon: <Users size={20} />,
        nameKey: 'headcountSnapshot',
        descKey: 'headcountSnapshotDesc',
    },
    {
        slug: 'leave-balances',
        icon: <Calendar size={20} />,
        nameKey: 'leaveBalancesReport',
        descKey: 'leaveBalancesReportDesc',
    },
    {
        slug: 'birthdays',
        icon: <Cake size={20} />,
        nameKey: 'birthdaysReport',
        descKey: 'birthdaysReportDesc',
    },
    {
        slug: 'work-anniversaries',
        icon: <Trophy size={20} />,
        nameKey: 'workAnniversaries',
        descKey: 'workAnniversariesDesc',
    },
    {
        slug: 'probation-upcoming',
        icon: <Clock size={20} />,
        nameKey: 'probationUpcoming',
        descKey: 'probationUpcomingDesc',
    },
    {
        slug: 'compensation-changes',
        icon: <Coins size={20} />,
        nameKey: 'compensationChanges',
        descKey: 'compensationChangesDesc',
        requiresPayroll: true,
    },
]

// Default the status to "active": custom reports are almost always about the
// current workforce (TASK-176). Inactive/on-leave can still be chosen.
const EMPTY_FILTERS: BuilderFilters = {
    teamIds: [],
    companyIds: [],
    status: 'active',
    startDate: '',
    endDate: '',
}

// ── Props ──

interface ReportsViewProps {
    lang: Language
    /** Whether the actor may see the salary-tier payroll-info report (create_shared_reports). */
    canViewPayroll: boolean
}

// ── Component ──

export const ReportsView = ({ lang, canViewPayroll }: ReportsViewProps) => {
    const [viewMode, setViewMode] = useState<ViewMode>('index')
    const [activeSlug, setActiveSlug] = useState<string | null>(null)
    const [activeReportName, setActiveReportName] = useState('')

    // Detail view state
    const [detailColumns, setDetailColumns] = useState<string[]>([])
    const [detailRows, setDetailRows] = useState<ReportRow[]>([])
    const [detailLabels, setDetailLabels] = useState<Record<string, string>>({})
    const [detailLoading, setDetailLoading] = useState(false)

    // Saved reports
    const [savedReportsList, setSavedReportsList] = useState<SavedReport[]>([])

    // Q12 results report (TASK-313)
    const [q12Meta, setQ12Meta] = useState<Q12ReportData | null>(null)
    const [q12Data, setQ12Data] = useState<Q12ReportData | null>(null)
    const [q12Loading, setQ12Loading] = useState(false)
    const [q12LinkCopied, setQ12LinkCopied] = useState(false)
    // One team's blocks only (null = every visible team); headline/company untouched.
    const [q12TeamFilter, setQ12TeamFilter] = useState<number | null>(null)

    // Builder state
    const [availableColumns, setAvailableColumns] = useState<ReportColumn[]>([])
    const [selectedColumns, setSelectedColumns] = useState<string[]>([])
    const [builderRows, setBuilderRows] = useState<ReportRow[]>([])
    const [builderColumns, setBuilderColumns] = useState<string[]>([])
    const [builderLabels, setBuilderLabels] = useState<Record<string, string>>({})
    const [builderLoading, setBuilderLoading] = useState(false)

    // Builder filters
    const [filters, setFilters] = useState<BuilderFilters>({ ...EMPTY_FILTERS })
    const [teams, setTeams] = useState<RefOption[]>([])
    const [companies, setCompanies] = useState<RefOption[]>([])

    // Save dialog
    const [showSave, setShowSave] = useState(false)
    const [saveName, setSaveName] = useState('')
    const [saving, setSaving] = useState(false)

    // Export dropdown
    const [exporting, setExporting] = useState(false)
    const [showExportMenu, setShowExportMenu] = useState(false)
    const exportMenuRef = useRef<HTMLDivElement>(null)

    // Payroll-info report (TASK-175) — defaults to the previous calendar month,
    // since salaries are paid in arrears.
    const [payrollYear, setPayrollYear] = useState(() =>
        new Date(
            Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth() - 1, 1),
        ).getUTCFullYear(),
    )
    const [payrollMonth, setPayrollMonth] = useState(
        () =>
            new Date(
                Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth() - 1, 1),
            ).getUTCMonth() + 1,
    )

    const r = t.portal.reports

    // Close export dropdown on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
                setShowExportMenu(false)
            }
        }
        if (showExportMenu) document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [showExportMenu])

    // Load saved reports + Q12 report visibility on mount
    useEffect(() => {
        void loadSavedReports()
        void loadQ12Meta()
    }, [])

    const loadSavedReports = async () => {
        const result = await portalGet<SavedReport[]>('/api/portal/reports/saved')
        if (result.ok) setSavedReportsList(result.data)
    }

    const loadQ12Meta = async () => {
        const result = await portalGet<Q12ReportData>('/api/portal/reports/q12?meta=1')
        if (result.ok) setQ12Meta(result.data)
    }

    const loadQ12Report = async (
        campaignId?: number,
        compareId?: number | 'none',
        groupId: number | null = q12TeamFilter,
    ) => {
        setQ12Loading(true)
        const params = new URLSearchParams()
        if (campaignId) params.set('campaign_id', String(campaignId))
        if (compareId !== undefined) params.set('compare_campaign_id', String(compareId))
        if (groupId !== null) params.set('group_id', String(groupId))
        const qs = params.toString()
        const result = await portalGet<Q12ReportData>(
            `/api/portal/reports/q12${qs ? `?${qs}` : ''}`,
        )
        if (result.ok) {
            setQ12Data(result.data)
            // Keep the filter in step with what the server resolved — a team
            // missing from the newly picked period falls back to the default.
            const selected = result.data.selectedGroupIds ?? []
            setQ12TeamFilter(selected.length === 1 ? selected[0] : null)
        }
        setQ12Loading(false)
    }

    const openQ12Report = async () => {
        setViewMode('q12')
        await loadQ12Report()
    }

    const loadReferenceData = async () => {
        const [teamResult, companyResult] = await Promise.all([
            portalGet<RefOption[]>('/api/portal/teams'),
            portalGet<RefOption[]>('/api/portal/companies'),
        ])
        if (teamResult.ok) setTeams(teamResult.data)
        if (companyResult.ok) setCompanies(companyResult.data)
    }

    // ── Build filters array for API ──

    const buildApiFilters = () => {
        const apiFilters: { field: string; operator: string; value: string | string[] }[] = []
        if (filters.teamIds.length > 0) {
            apiFilters.push({
                field: 'team_id',
                operator: 'in',
                value: filters.teamIds.map(String),
            })
        }
        if (filters.companyIds.length > 0) {
            apiFilters.push({
                field: 'company_id',
                operator: 'in',
                value: filters.companyIds.map(String),
            })
        }
        if (filters.status) {
            apiFilters.push({ field: 'status', operator: 'eq', value: filters.status })
        }
        return apiFilters
    }

    // Reporting period for the time-away (days-taken / details) columns. Empty →
    // the server defaults to the current calendar year.
    const buildPeriod = () => ({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
    })

    const hasActiveFilters =
        filters.teamIds.length > 0 ||
        filters.companyIds.length > 0 ||
        filters.status !== EMPTY_FILTERS.status ||
        filters.startDate !== '' ||
        filters.endDate !== ''

    // ── Standard report click ──

    const openStandardReport = async (slug: string, name: string) => {
        setActiveSlug(slug)
        setActiveReportName(name)
        setViewMode('detail')
        setFilters({ ...EMPTY_FILTERS })
        void loadReferenceData()
        await loadStandardReport(slug, { ...EMPTY_FILTERS })
    }

    // Standard reports reuse the builder's filter panel. Relational ids go on the
    // query string comma-separated; status / date range are scalar params.
    const loadStandardReport = async (slug: string, f: BuilderFilters) => {
        setDetailLoading(true)
        const params = new URLSearchParams()
        if (f.teamIds.length > 0) params.set('team_id', f.teamIds.join(','))
        if (f.companyIds.length > 0) params.set('company_id', f.companyIds.join(','))
        if (f.status) params.set('status', f.status)
        if (f.startDate) params.set('start_date', f.startDate)
        if (f.endDate) params.set('end_date', f.endDate)
        const qs = params.toString()
        const result = await portalGet<{
            columns: string[]
            rows: ReportRow[]
            labels?: Record<string, string>
        }>(`/api/portal/reports/standard/${slug}${qs ? `?${qs}` : ''}`)
        if (result.ok) {
            setDetailColumns(result.data.columns)
            setDetailRows(result.data.rows)
            setDetailLabels(result.data.labels ?? {})
        }
        setDetailLoading(false)
    }

    // ── Saved report click ──

    const openSavedReport = async (report: SavedReport) => {
        const config = JSON.parse(report.config) as { columns: string[] }
        setActiveSlug(null)
        setActiveReportName(report.name)
        setViewMode('detail')
        setDetailLoading(true)
        const result = await portalPost<{
            columns: string[]
            rows: ReportRow[]
            labels?: Record<string, string>
        }>('/api/portal/reports/builder/preview', { columns: config.columns })
        if (result.ok) {
            setDetailColumns(result.data.columns)
            setDetailRows(result.data.rows)
            setDetailLabels(result.data.labels ?? {})
        }
        setDetailLoading(false)
    }

    const deleteSavedReport = async (id: number) => {
        await portalDelete(`/api/portal/reports/saved/${id}`)
        await loadSavedReports()
    }

    // ── Builder ──

    const openBuilder = async () => {
        setViewMode('builder')
        setSelectedColumns([])
        setBuilderRows([])
        setBuilderColumns([])
        setFilters({ ...EMPTY_FILTERS })
        const result = await portalGet<ReportColumn[]>(
            '/api/portal/reports/builder/available-columns',
        )
        if (result.ok) setAvailableColumns(result.data)
        await loadReferenceData()
    }

    const toggleColumn = (key: string) => {
        setSelectedColumns((prev) =>
            prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key],
        )
    }

    const previewBuilder = async () => {
        if (selectedColumns.length === 0) return
        setBuilderLoading(true)
        const result = await portalPost<{
            columns: string[]
            rows: ReportRow[]
            labels?: Record<string, string>
        }>('/api/portal/reports/builder/preview', {
            columns: selectedColumns,
            filters: buildApiFilters(),
            period: buildPeriod(),
        })
        if (result.ok) {
            setBuilderColumns(result.data.columns)
            setBuilderRows(result.data.rows)
            setBuilderLabels(result.data.labels ?? {})
        }
        setBuilderLoading(false)
    }

    const saveBuilderReport = async () => {
        if (!saveName.trim()) return
        setSaving(true)
        await portalPost('/api/portal/reports/saved', {
            name: saveName.trim(),
            config: { columns: selectedColumns },
        })
        setShowSave(false)
        setSaveName('')
        await loadSavedReports()
        setSaving(false)
    }

    // ── Export ──

    const exportReport = async (format: 'csv' | 'xlsx' | 'pdf') => {
        setExporting(true)
        setShowExportMenu(false)
        const body: Record<string, unknown> = { format, reportName: activeReportName }

        if (activeSlug) {
            body.type = 'standard'
            body.slug = activeSlug
            body.filters = buildApiFilters()
            body.period = buildPeriod()
        } else {
            const cols = viewMode === 'builder' ? selectedColumns : detailColumns
            body.type = 'builder'
            body.columns = cols
            body.filters = buildApiFilters()
            body.period = buildPeriod()
        }

        const ext = format === 'xlsx' ? 'xlsx' : format
        const filename = `${activeReportName || 'report'}-${new Date().toISOString().slice(0, 10)}.${ext}`
        await portalDownload('/api/portal/reports/export-report', body, filename)
        setExporting(false)
    }

    // Q12 export follows the period, comparison and team on screen. CSV and
    // Excel come from the server, which re-applies the viewer's scope and
    // redaction; PDF prints the on-screen view itself (charts included).
    const exportQ12Report = async (format: 'csv' | 'xlsx' | 'pdf') => {
        if (!q12Data?.campaign) return
        setShowExportMenu(false)
        const baseName = `${r.q12Report(lang)} ${q12Data.campaign.name}-${new Date().toISOString().slice(0, 10)}`
        if (format === 'pdf') {
            printPage(baseName)
            return
        }
        setExporting(true)
        const filename = `${baseName}.${format}`
        await portalDownload(
            '/api/portal/reports/q12/export',
            {
                format,
                lang,
                campaign_id: q12Data.campaign.id,
                compare_campaign_id: q12Data.compareCampaign?.id ?? 'none',
                group_id: q12TeamFilter,
            },
            filename,
        )
        setExporting(false)
    }

    // ── Back ──

    const backToIndex = () => {
        setViewMode('index')
        setActiveSlug(null)
        setActiveReportName('')
        setDetailColumns([])
        setDetailRows([])
    }

    // ── Export dropdown renderer ──

    function renderExportDropdown(
        disabled: boolean,
        onExport: (format: 'csv' | 'xlsx' | 'pdf') => Promise<void> = exportReport,
    ) {
        return (
            <div className="portal-reports__export-dropdown" ref={exportMenuRef}>
                <Button
                    size="small"
                    variant="secondary"
                    text={exporting ? '...' : r.exportDropdown(lang)}
                    isDisabled={disabled || exporting}
                    onClick={() => setShowExportMenu(!showExportMenu)}
                />
                {showExportMenu && (
                    <div className="portal-reports__export-menu">
                        <button
                            className="portal-reports__export-menu-item"
                            onClick={() => void onExport('csv')}
                        >
                            {r.exportCsv(lang)}
                        </button>
                        <button
                            className="portal-reports__export-menu-item"
                            onClick={() => void onExport('xlsx')}
                        >
                            {r.exportXlsx(lang)}
                        </button>
                        <button
                            className="portal-reports__export-menu-item"
                            onClick={() => void onExport('pdf')}
                        >
                            {r.exportPdf(lang)}
                        </button>
                    </div>
                )}
            </div>
        )
    }

    // ── Render ──

    if (viewMode === 'detail') {
        return renderDetailView()
    }
    if (viewMode === 'builder') {
        return renderBuilderView()
    }
    if (viewMode === 'payroll') {
        return renderPayrollView()
    }
    if (viewMode === 'q12') {
        return renderQ12View()
    }
    return renderIndexView()

    // ── Index ──

    function renderIndexView() {
        return (
            <PageShell>
                <PageHeader
                    title={r.title(lang)}
                    actions={
                        <Button
                            text={r.createCustomReport(lang)}
                            iconRight={ArrowRight}
                            size="small"
                            onClick={openBuilder}
                        />
                    }
                />

                {/* Standard Reports */}
                <section>
                    <h2 className="portal-reports__section-title">{r.standardReports(lang)}</h2>
                    <div className="portal-reports__grid">
                        {STANDARD_REPORTS.filter(
                            (report) => !report.requiresPayroll || canViewPayroll,
                        ).map((report) => {
                            const nameTranslation = r[report.nameKey]
                            const descTranslation = r[report.descKey]
                            const name =
                                typeof nameTranslation === 'function'
                                    ? (nameTranslation as (l: Language) => string)(lang)
                                    : ''
                            const desc =
                                typeof descTranslation === 'function'
                                    ? (descTranslation as (l: Language) => string)(lang)
                                    : ''
                            return (
                                <button
                                    key={report.slug}
                                    className="portal-reports__report-card"
                                    onClick={() => openStandardReport(report.slug, name)}
                                >
                                    <span className="portal-reports__report-card-icon">
                                        {report.icon}
                                    </span>
                                    <h3 className="portal-reports__report-card-title">{name}</h3>
                                    <p className="portal-reports__report-card-desc">{desc}</p>
                                </button>
                            )
                        })}
                        {/* Payroll-info report (salary tier) — TASK-175. A card link,
                            like the other standard reports, opening its own view. */}
                        {canViewPayroll && (
                            <button
                                className="portal-reports__report-card"
                                onClick={() => setViewMode('payroll')}
                            >
                                <span className="portal-reports__report-card-icon">
                                    <Receipt size={20} />
                                </span>
                                <h3 className="portal-reports__report-card-title">
                                    {r.payrollReport(lang)}
                                </h3>
                                <p className="portal-reports__report-card-desc">
                                    {r.payrollReportDesc(lang)}
                                </p>
                            </button>
                        )}
                        {/* Q12 results (TASK-313) — dual-gated server-side: shown
                            only when the actor holds both the Q12 app role and a
                            portal Admin/Manager role. */}
                        {q12Meta && q12Meta.scope !== 'none' && q12Meta.campaigns.length > 0 && (
                            <button
                                className="portal-reports__report-card"
                                onClick={() => void openQ12Report()}
                            >
                                <span className="portal-reports__report-card-icon">
                                    <Smile size={20} />
                                </span>
                                <h3 className="portal-reports__report-card-title">
                                    {r.q12Report(lang)}
                                </h3>
                                <p className="portal-reports__report-card-desc">
                                    {r.q12ReportDesc(lang)}
                                </p>
                            </button>
                        )}
                    </div>
                </section>

                {/* Saved Reports */}
                <section>
                    <h2 className="portal-reports__section-title">{r.savedReports(lang)}</h2>
                    {savedReportsList.length === 0 ? (
                        <p className="portal-reports__empty-state">{r.noSavedReports(lang)}</p>
                    ) : (
                        <div className="portal-reports__saved-list">
                            {savedReportsList.map((report) => (
                                <div key={report.id} className="portal-reports__saved-item">
                                    <div>
                                        <span className="portal-reports__saved-name">
                                            {report.name}
                                        </span>
                                        <span className="portal-reports__saved-date">
                                            {' — '}
                                            {report.updated_at_utc?.slice(0, 10)}
                                        </span>
                                    </div>
                                    <div className="portal-reports__saved-actions">
                                        <Button
                                            size="small"
                                            variant="secondary"
                                            text={r.openReport(lang)}
                                            onClick={() => void openSavedReport(report)}
                                        />
                                        <Button
                                            size="small"
                                            variant="alert"
                                            text={r.deleteReport(lang)}
                                            onClick={() => void deleteSavedReport(report.id)}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </PageShell>
        )
    }

    // ── Detail ──

    function renderDetailView() {
        return (
            <PageShell>
                <div className="portal-reports__detail">
                    <div className="portal-reports__detail-header">
                        <div>
                            <Button
                                className="portal-reports__back-btn"
                                size="small"
                                variant="tertiary"
                                iconLeft={ArrowLeft}
                                text={r.backToReports(lang)}
                                onClick={backToIndex}
                            />
                            <h1 className="portal-reports__title">{activeReportName}</h1>
                        </div>
                        {!detailLoading && detailRows.length > 0 && (
                            <div className="portal-reports__export-group">
                                {renderExportDropdown(false)}
                            </div>
                        )}
                    </div>

                    {activeSlug &&
                        renderFilterPanel(() => void loadStandardReport(activeSlug, filters))}

                    {detailLoading ? (
                        <div className="portal-reports__loading">{t.common.loading(lang)}</div>
                    ) : (
                        <>
                            <p className="portal-reports__preview-info">
                                {r.rowCount(detailRows.length)(lang)}
                            </p>
                            {renderTable(detailColumns, detailRows, detailLabels)}
                        </>
                    )}
                </div>
            </PageShell>
        )
    }

    // ── Payroll-info report (TASK-175) ──

    function renderPayrollView() {
        const monthName = (m: number) => {
            const formatted = new Intl.DateTimeFormat(lang === 'et' ? 'et-EE' : 'en-GB', {
                month: 'long',
            }).format(new Date(Date.UTC(2000, m - 1, 1)))
            return formatted.charAt(0).toUpperCase() + formatted.slice(1)
        }
        const monthOptions = Array.from({ length: 12 }, (_, i) => ({
            value: String(i + 1),
            label: monthName(i + 1),
        }))
        const thisYear = new Date().getUTCFullYear()
        const yearOptions = [thisYear, thisYear - 1, thisYear - 2].map((y) => ({
            value: String(y),
            label: String(y),
        }))
        const openPayrollPreview = () =>
            window.open(
                `/api/portal/reports/payroll-preview?year=${payrollYear}&month=${payrollMonth}`,
                '_blank',
                'noopener',
            )
        return (
            <PageShell>
                <div className="portal-reports__detail">
                    <div className="portal-reports__detail-header">
                        <div>
                            <Button
                                className="portal-reports__back-btn"
                                size="small"
                                variant="tertiary"
                                iconLeft={ArrowLeft}
                                text={r.backToReports(lang)}
                                onClick={backToIndex}
                            />
                            <h1 className="portal-reports__title">{r.payrollReport(lang)}</h1>
                        </div>
                    </div>
                    <p className="portal-reports__preview-info">{r.payrollReportDesc(lang)}</p>
                    <div className="portal-reports__payroll-controls">
                        <Select
                            wrapperClass="portal-reports__filter-field"
                            label={r.payrollMonth(lang)}
                            value={String(payrollMonth)}
                            onChange={(value) => setPayrollMonth(Number(value))}
                            options={monthOptions}
                        />
                        <Select
                            wrapperClass="portal-reports__filter-field"
                            label={r.payrollYear(lang)}
                            value={String(payrollYear)}
                            onChange={(value) => setPayrollYear(Number(value))}
                            options={yearOptions}
                        />
                        <Button text={r.payrollOpenPreview(lang)} onClick={openPayrollPreview} />
                    </div>
                </div>
            </PageShell>
        )
    }

    // ── Q12 results (TASK-313) ──

    function renderQ12View() {
        const campaignOptions = (q12Data?.campaigns ?? []).map((c) => {
            const parts = [`${c.name} (${formatCellValue(c.start)} – ${formatCellValue(c.end)})`]
            if (c.isOpen) parts.push(r.q12OpenMarker(lang))
            if (c.progress) {
                parts.push(r.q12Answered(c.progress.respondents, c.progress.eligible)(lang))
            }
            return { value: String(c.id), label: parts.join(' · ') }
        })
        const compareOptions = [
            { value: 'none', label: r.q12CompareNone(lang) },
            ...(q12Data?.campaigns ?? [])
                .filter((c) => c.id !== q12Data?.campaign?.id)
                .map((c) => ({ value: String(c.id), label: c.name })),
        ]
        const activeCampaignId = q12Data?.campaign?.id
        const isAdminScope = q12Data?.scope === 'all'
        const q12Teams = q12Data?.teams ?? []
        const selectedGroupIds = q12Data?.selectedGroupIds ?? []
        // The comparison is always sent explicitly: switching period keeps
        // the user's choice (dropping it only when it would equal the new
        // period) and never picks one on its own.
        const currentCompare = q12Data?.compareCampaign
            ? String(q12Data.compareCampaign.id)
            : 'none'
        const reload = (
            campaignId: number,
            compareValue: string = currentCompare,
            groupId: number | null = q12TeamFilter,
        ) =>
            void loadQ12Report(
                campaignId,
                compareValue === 'none' || Number(compareValue) === campaignId
                    ? 'none'
                    : Number(compareValue),
                groupId,
            )
        // One team is picked automatically; otherwise the viewer chooses (or none).
        const showTeamFilter = q12Teams.length > 0 && (isAdminScope || q12Teams.length > 1)
        const teamFilterValue = selectedGroupIds.length === 1 ? String(selectedGroupIds[0]) : ''
        const copyQ12Link = (url: string) => {
            void navigator.clipboard.writeText(url)
            setQ12LinkCopied(true)
            setTimeout(() => setQ12LinkCopied(false), 2000)
        }
        // The filters are hidden on paper, so the PDF states them in one line.
        const printSelection = q12Data?.campaign
            ? [
                  `${r.q12Campaign(lang)}: ${q12Data.campaign.name} (${formatCellValue(q12Data.campaign.start)} – ${formatCellValue(q12Data.campaign.end)})`,
                  `${r.q12Compare(lang)}: ${q12Data.compareCampaign?.name ?? r.q12CompareNone(lang)}`,
                  ...(q12Teams.length > 0
                      ? [
                            `${r.q12TeamFilter(lang)}: ${
                                q12Teams
                                    .filter((team) => selectedGroupIds.includes(team.id))
                                    .map((team) => team.name)
                                    .join(', ') || r.q12NoTeamSelected(lang)
                            }`,
                        ]
                      : []),
              ].join(' · ')
            : ''
        return (
            <PageShell>
                <div className="portal-reports__detail portal-reports__detail--q12">
                    <div className="portal-reports__detail-header">
                        <div>
                            <Button
                                className="portal-reports__back-btn"
                                size="small"
                                variant="tertiary"
                                iconLeft={ArrowLeft}
                                text={r.backToReports(lang)}
                                onClick={backToIndex}
                            />
                            <h1 className="portal-reports__title">{r.q12Report(lang)}</h1>
                            {printSelection && (
                                <p className="portal-reports__print-meta">{printSelection}</p>
                            )}
                        </div>
                        {!q12Loading && q12Data?.campaign && (
                            <div className="portal-reports__export-group">
                                {renderExportDropdown(false, exportQ12Report)}
                            </div>
                        )}
                    </div>

                    {q12Data && (
                        <div className="portal-reports__payroll-controls">
                            <Select
                                wrapperClass="portal-reports__filter-field"
                                label={r.q12Campaign(lang)}
                                value={activeCampaignId ? String(activeCampaignId) : ''}
                                onChange={(value) => reload(Number(value))}
                                options={campaignOptions}
                            />
                            <Select
                                wrapperClass="portal-reports__filter-field"
                                label={r.q12Compare(lang)}
                                value={
                                    q12Data.compareCampaign
                                        ? String(q12Data.compareCampaign.id)
                                        : 'none'
                                }
                                onChange={(value) =>
                                    activeCampaignId && reload(activeCampaignId, value)
                                }
                                options={compareOptions}
                            />
                            {showTeamFilter && (
                                <Select
                                    wrapperClass="portal-reports__filter-field"
                                    label={r.q12TeamFilter(lang)}
                                    value={teamFilterValue}
                                    onChange={(value) =>
                                        activeCampaignId &&
                                        reload(
                                            activeCampaignId,
                                            currentCompare,
                                            value ? Number(value) : null,
                                        )
                                    }
                                    options={[
                                        { value: '', label: r.q12NoTeamSelected(lang) },
                                        ...q12Teams.map((team) => ({
                                            value: String(team.id),
                                            label: team.name,
                                        })),
                                    ]}
                                />
                            )}
                        </div>
                    )}

                    {q12Loading || !q12Data ? (
                        <div className="portal-reports__loading">{t.common.loading(lang)}</div>
                    ) : (
                        <Q12ReportBody
                            data={q12Data}
                            lang={lang}
                            labels={r}
                            formatDate={formatCellValue}
                            surveyOrigin={window.location.origin}
                            linkCopy={{ copied: q12LinkCopied, onCopy: copyQ12Link }}
                            onSelectCampaign={(campaignId) => reload(campaignId)}
                        />
                    )}
                </div>
            </PageShell>
        )
    }

    // ── Builder ──

    function renderBuilderView() {
        const columnsByCategory = groupByCategory(availableColumns)

        return (
            <PageShell>
                <div className="portal-reports__detail-header">
                    <div>
                        <Button
                            className="portal-reports__back-btn"
                            size="small"
                            variant="tertiary"
                            iconLeft={ArrowLeft}
                            text={r.backToReports(lang)}
                            onClick={backToIndex}
                        />
                        <h1 className="portal-reports__title">{r.reportBuilder(lang)}</h1>
                    </div>
                </div>

                {renderFilterPanel()}

                <div className="portal-reports__builder">
                    {/* Column selector */}
                    <div className="portal-reports__column-selector">
                        <h3 className="portal-reports__section-title">{r.selectColumns(lang)}</h3>
                        {Object.entries(columnsByCategory).map(([category, cols]) => (
                            <div key={category} className="portal-reports__column-category">
                                <span className="portal-reports__category-title">
                                    {getCategoryLabel(category, lang)}
                                </span>
                                {cols.map((col) => (
                                    <label key={col.key} className="portal-reports__column-option">
                                        <input
                                            type="checkbox"
                                            checked={selectedColumns.includes(col.key)}
                                            onChange={() => toggleColumn(col.key)}
                                        />
                                        {col.label}
                                    </label>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* Preview */}
                    <div className="portal-reports__preview">
                        <div className="portal-reports__preview-actions">
                            <Button
                                size="small"
                                variant="primary"
                                text={r.previewReport(lang)}
                                isDisabled={selectedColumns.length === 0 || builderLoading}
                                onClick={previewBuilder}
                            />
                            {builderRows.length > 0 && (
                                <>
                                    <Button
                                        size="small"
                                        variant="secondary"
                                        text={r.saveReport(lang)}
                                        onClick={() => setShowSave(true)}
                                    />
                                    {renderExportDropdown(false)}
                                </>
                            )}
                            <span className="portal-reports__preview-info">
                                {selectedColumns.length === 0
                                    ? r.noColumnsSelected(lang)
                                    : r.rowCount(builderRows.length)(lang)}
                            </span>
                        </div>

                        {/* Save dialog */}
                        {showSave && (
                            <div className="portal-reports__save-modal">
                                <TextField
                                    className="portal-reports__save-input"
                                    placeholder={r.reportName(lang)}
                                    value={saveName}
                                    onChange={(value) => setSaveName(value)}
                                    onEnter={() => void saveBuilderReport()}
                                />
                                <Button
                                    size="small"
                                    variant="primary"
                                    text={saving ? '...' : r.saveReport(lang)}
                                    isDisabled={saving || !saveName.trim()}
                                    onClick={saveBuilderReport}
                                />
                                <Button
                                    size="small"
                                    variant="tertiary"
                                    iconOnly
                                    iconLeft={X}
                                    text={t.portal.settings.close(lang)}
                                    onClick={() => setShowSave(false)}
                                />
                            </div>
                        )}

                        {/* Results table */}
                        {builderLoading ? (
                            <div className="portal-reports__loading">{t.common.loading(lang)}</div>
                        ) : builderRows.length > 0 ? (
                            renderTable(builderColumns, builderRows, builderLabels)
                        ) : selectedColumns.length > 0 ? (
                            <div className="portal-reports__empty-state">{r.noData(lang)}</div>
                        ) : null}
                    </div>
                </div>
            </PageShell>
        )
    }

    // ── Filter panel renderer ──

    // Shared by the custom builder and the standard-report detail view. The
    // builder re-runs via its "Preview" button, so it passes no `onApply`; the
    // detail view passes a callback to re-fetch the standard report.
    function renderFilterPanel(onApply?: () => void) {
        return (
            <div className="portal-reports__filter-panel">
                <h3 className="portal-reports__filter-title">{r.filters(lang)}</h3>
                <div className="portal-reports__filter-row">
                    <MultiSelect
                        wrapperClass="portal-reports__filter-field"
                        label={r.filterTeam(lang)}
                        placeholder={r.allTeamsFilter(lang)}
                        value={filters.teamIds.map(String)}
                        onChange={(values) =>
                            setFilters((f) => ({ ...f, teamIds: values.map(Number) }))
                        }
                        options={teams.map((tm) => ({ value: String(tm.id), label: tm.name }))}
                    />

                    <MultiSelect
                        wrapperClass="portal-reports__filter-field"
                        label={r.filterCompany(lang)}
                        placeholder={r.allCompaniesFilter(lang)}
                        value={filters.companyIds.map(String)}
                        onChange={(values) =>
                            setFilters((f) => ({ ...f, companyIds: values.map(Number) }))
                        }
                        options={companies.map((co) => ({ value: String(co.id), label: co.name }))}
                    />

                    <Select
                        wrapperClass="portal-reports__filter-field"
                        label={r.filterStatus(lang)}
                        placeholder={r.allStatuses(lang)}
                        value={filters.status ?? ''}
                        onChange={(value) => setFilters((f) => ({ ...f, status: value || null }))}
                        options={[
                            { value: 'active', label: r.statusActive(lang) },
                            { value: 'inactive', label: r.statusInactive(lang) },
                            { value: 'on_leave', label: r.statusOnLeave(lang) },
                        ]}
                    />

                    <TextField
                        wrapperClass="portal-reports__filter-field"
                        label={r.filterStartDate(lang)}
                        value={filters.startDate}
                        onChange={(value) => setFilters((f) => ({ ...f, startDate: value }))}
                        inputProps={{ type: 'date' }}
                    />

                    <TextField
                        wrapperClass="portal-reports__filter-field"
                        label={r.filterEndDate(lang)}
                        value={filters.endDate}
                        onChange={(value) => setFilters((f) => ({ ...f, endDate: value }))}
                        inputProps={{ type: 'date' }}
                    />

                    {onApply && (
                        <Button
                            size="small"
                            variant="primary"
                            text={r.applyFilters(lang)}
                            onClick={onApply}
                        />
                    )}

                    {hasActiveFilters && (
                        <Button
                            size="small"
                            variant="tertiary"
                            text={r.clearFilters(lang)}
                            onClick={() => setFilters({ ...EMPTY_FILTERS })}
                        />
                    )}
                </div>
            </div>
        )
    }

    // ── Table renderer ──

    function renderTable(columns: string[], rows: ReportRow[], labels: Record<string, string>) {
        if (columns.length === 0) return null
        return (
            <div className="portal-reports__table-wrap">
                <table className="portal-reports__table">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th key={col}>
                                    {labels[col] ?? t.portal.reports.unknownColumn(col)(lang)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={i}>
                                {columns.map((col) => (
                                    <td key={col}>{formatCellValue(row[col])}</td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }
}

// ── Helpers ──

// ISO date (YYYY-MM-DD, optionally followed by a time) — rendered as DD.MM.YYYY
// to match the app-wide date convention (TASK-176).
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}|$)/

const formatCellValue = (v: unknown): string => {
    if (v === null || v === undefined) return '—'
    const s = `${v as string | number | boolean}`
    if (ISO_DATE_RE.test(s)) {
        const [y, m, d] = s.slice(0, 10).split('-')
        return `${d}.${m}.${y}`
    }
    return s
}

const groupByCategory = (columns: ReportColumn[]): Record<string, ReportColumn[]> => {
    const groups: Record<string, ReportColumn[]> = {}
    for (const col of columns) {
        const key = col.category
        if (!groups[key]) groups[key] = []
        groups[key].push(col)
    }
    return groups
}

const getCategoryLabel = (category: string, lang: Language): string => {
    const map: Record<string, keyof typeof t.portal.reports> = {
        basics: 'categoryBasics',
        employment: 'categoryEmployment',
        banking: 'categoryBanking',
        compensation: 'categoryCompensation',
        social: 'categorySocial',
        leave_days: 'categoryLeaveDays',
        leave_details: 'categoryLeaveDetails',
    }
    const key = map[category]
    if (key) {
        const val = t.portal.reports[key]
        if (typeof val === 'function') return (val as (l: Language) => string)(lang)
    }
    return t.portal.reports.unknownCategory(category)(lang)
}
